import React from "react";
import { Info, PackageCheck, PackageX } from "lucide-react";

/**
 * Product item rendered by the restock selection step.
 * `uuid` corresponds to `uuid_sale_product` on the API side — must match exactly
 * for the backend to apply the restock decision per item.
 */
export interface RestockSelectionProduct {
  uuid: string;
  name: string;
  quantity: number;
  price?: number;
  /**
   * True when the product has at least one active inventory supply (insumo) linked.
   * When `false`, the Sí/No toggle is replaced by an informative message (nothing to restock).
   * `undefined` (older API responses) keeps the toggle visible as a safe fallback.
   */
  has_supplies?: boolean;
}

export interface RestockSelectionStepProps {
  /** Products that will be cancelled in this flow. */
  products: RestockSelectionProduct[];
  /**
   * Map keyed by `uuid_sale_product` → restock decision.
   * `true` = regresar al stock (default histórico); `false` = no reinventariar.
   */
  decisions: Record<string, boolean>;
  /** Callback invoked when the operator toggles a product. */
  onChange: (uuid: string, restock: boolean) => void;
  /** Optional descriptive copy shown above the list. */
  description?: string;
}

/**
 * Restock selection step used by every Dashboard cancellation flow (sale full/partial, bill full/partial).
 * Lists each cancelled product with a binary "regresar al stock / no regresar" toggle.
 * Defaults are owned by the parent (per spec: preserve current "always restock" behavior when undefined).
 */
export const RestockSelectionStep: React.FC<RestockSelectionStepProps> = ({
  products,
  decisions,
  onChange,
  description = "Elige por cada producto si debe regresar al inventario o no. Solo aplica para cancelaciones desde Dashboard.",
}) => {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{description}</p>
      <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
        {products.map((product: RestockSelectionProduct) => {
          const blnRestock: boolean = decisions[product.uuid] !== false;
          // Products without insumos have nothing to return to stock — show a message instead of the toggle.
          const blnHasSupplies: boolean = product.has_supplies !== false;
          return (
            <div
              key={product.uuid}
              className={`flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${
                !blnHasSupplies
                  ? "border-gray-200 bg-gray-50"
                  : blnRestock ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">
                  {product.name}
                </p>
                <p className="text-xs text-gray-500">
                  Cantidad: {product.quantity}
                  {typeof product.price === "number"
                    ? ` · $${product.price.toFixed(2)} c/u`
                    : ""}
                </p>
              </div>
              {!blnHasSupplies ? (
                <div className="flex shrink-0 items-center gap-1.5 text-xs text-gray-500 sm:max-w-[220px]">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Este producto no contiene insumos para reinventariar.
                  </span>
                </div>
              ) : (
              <div
                role="radiogroup"
                aria-label={`Reinventariar ${product.name}`}
                className="flex shrink-0 gap-2"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={blnRestock}
                  className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                    blnRestock
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => onChange(product.uuid, true)}
                >
                  <PackageCheck className="h-3.5 w-3.5" />
                  Sí
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!blnRestock}
                  className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                    !blnRestock
                      ? "border-amber-500 bg-amber-500 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => onChange(product.uuid, false)}
                >
                  <PackageX className="h-3.5 w-3.5" />
                  No regresar
                </button>
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
